import { useMemo, useState } from 'react';
import { Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PathEnum } from 'core/router/path.enum';
import usePagination from 'core/hooks/usePagination';
import { LocationFilterEnum } from 'enums/global.enum';
import { StorageKeysEnum } from 'enums/storageKeys.enum';
import { useAppSelector } from 'core/store/hooks';
import { formatCurrency } from 'shared/utils/common.utils';
import NoData from 'shared/design-system/components/no-data-box';
import AppAccordion from 'shared/design-system/components/app-accordion';
import AppPaginator from 'shared/design-system/components/app-paginator';
import ExcelExport from 'shared/design-system/components/excel-export';
import useFormDashboardStorico from 'shared/design-system/components/app-accordion/_hooks/useFormDashboardStorico';
import FiltersDashboardStorico, { filtersDashboardStoricoData } from '../_components/filters-dashboard-storico';
import useStoricoConfronto, { StoricoConfrontoRow } from '../_hooks/storico-hooks/useStoricoConfronto';
import { filtersDashboardStoricoUsedSelector } from '../_redux/selectors';
import { dashboardConfig } from '../_config/dashboard.config';
import './styles.scss';

const PAGE_SIZE = dashboardConfig.defaultPageSize;

type SortKey = keyof Pick<StoricoConfrontoRow, 'progetto' | 'valoreConfronto' | 'valoreAttuale' | 'gap' | 'gapPerc'>;

const formatEuro = (value: number) => `${value < 0 ? '-' : ''}€ ${formatCurrency(Math.abs(value), 0)}`;
const formatPerc = (value: number | null) => (value === null ? '-' : `${formatCurrency(value * 100, 1)}%`);
const formatDate = (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY') : '-');
// Un gap negativo (in rosso) è un peggioramento
const gapClass = (value: number | null) => (value !== null && value < 0 ? 'text-danger' : '');

const StoricoTable: React.FC = () => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({ key: 'gap', asc: true });

  const { onSubmit, onReset, chipsFormData, setChipsFormData, reset } =
    useFormDashboardStorico(filtersDashboardStoricoData);

  const { rows, totali, dataAttuale, dataConfronto, isLoading, noConfrontoSnapshot } = useStoricoConfronto();

  const filtersUsed = useAppSelector(filtersDashboardStoricoUsedSelector);
  // Senza chiave selezionata ci sono più misure: i totali sono uno per misura
  const allMeasures = !filtersUsed?.fkChiaveMisura;

  const sortedRows = useMemo(() => {
    const direction = sort.asc ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = a[sort.key];
      const vb = b[sort.key];
      if (typeof va === 'string' || typeof vb === 'string') {
        return String(va).localeCompare(String(vb)) * direction;
      }
      // I gap % non calcolabili (confronto a 0) vanno sempre in fondo
      if (va === null) return 1;
      if (vb === null) return -1;
      return ((va as number) - (vb as number)) * direction;
    });
  }, [rows, sort]);

  const { pageNumber, setPageNumber, totalPages, visibleItems } = usePagination(sortedRows, PAGE_SIZE);

  const excelData = useMemo(
    () =>
      sortedRows.map((r) => ({
        [t('dashboard.storico.table-titles.project')]: r.progetto,
        [t('dashboard.storico.table-titles.comparison-value')]: r.valoreConfronto,
        [t('dashboard.storico.table-titles.current-value')]: r.valoreAttuale,
        [t('dashboard.storico.table-titles.gap')]: r.gap,
        [t('dashboard.storico.table-titles.gap-perc')]: r.gapPerc,
      })),
    [sortedRows, t]
  );

  // Le date effettive del confronto sono nell'intestazione delle colonne
  const columns: { key: SortKey; title: string; numeric?: boolean }[] = [
    { key: 'progetto', title: t('dashboard.storico.table-titles.project') },
    {
      key: 'valoreConfronto',
      title: `${t('dashboard.storico.table-titles.comparison-value')} (${formatDate(dataConfronto)})`,
      numeric: true,
    },
    {
      key: 'valoreAttuale',
      title: `${t('dashboard.storico.table-titles.current-value')} (${formatDate(dataAttuale)})`,
      numeric: true,
    },
    { key: 'gap', title: t('dashboard.storico.table-titles.gap'), numeric: true },
    { key: 'gapPerc', title: t('dashboard.storico.table-titles.gap-perc'), numeric: true },
  ];

  const handleSort = (key: SortKey) => {
    setSort((prev) => ({ key, asc: prev.key === key ? !prev.asc : true }));
  };

  const handleReset = () => {
    onReset();
    chipsFormData && setChipsFormData([]);
  };

  return (
    <div>
      <AppAccordion
        formData={filtersDashboardStoricoData}
        location={LocationFilterEnum.DASHBOARD_STORICO}
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        chipsFormData={chipsFormData}
        setChipsFormData={setChipsFormData}
        reset={reset}
      >
        <FiltersDashboardStorico onSubmit={onSubmit} handleReset={handleReset} />
      </AppAccordion>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-3">
          <NoData
            msg={t(noConfrontoSnapshot ? 'dashboard.storico.no-comparison-snapshot' : 'dashboard.storico.no-data')}
          />
        </div>
      ) : (
        <div className="storico-card mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="storico-title mb-0">{t('dashboard.storico.title')}</h6>
            <ExcelExport data={excelData} fileName="Storico" />
          </div>

          <div className="storico-table-container">
            <Table hover size="sm" className="storico-table mb-0">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      role="button"
                      className={column.numeric ? 'text-end' : ''}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.title}
                      <i
                        className={`fas ms-1 ${
                          sort.key === column.key ? (sort.asc ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort text-muted'
                        }`}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((row: StoricoConfrontoRow) => (
                  <tr key={row.id}>
                    <td>
                      <a
                        href={`${window.location.origin}${window.location.pathname}#/${PathEnum.PRIVATE}/${PathEnum.PROGETTI}/${row.fkProgetto}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-links"
                      >
                        {row.progetto}
                      </a>
                    </td>
                    <td className="text-end">{formatEuro(row.valoreConfronto)}</td>
                    <td className="text-end">{formatEuro(row.valoreAttuale)}</td>
                    <td className={`text-end ${gapClass(row.gap)}`}>{formatEuro(row.gap)}</td>
                    <td className={`text-end ${gapClass(row.gapPerc)}`}>{formatPerc(row.gapPerc)}</td>
                  </tr>
                ))}
              </tbody>
              {totali.length > 0 && (
                <tfoot>
                  {totali.map((tot) => (
                    <tr key={tot.misura}>
                      <td>
                        {allMeasures ? `${t('dashboard.storico.total')} · ${tot.misura}` : t('dashboard.storico.total')}
                      </td>
                      <td className="text-end">{formatEuro(tot.valoreConfronto)}</td>
                      <td className="text-end">{formatEuro(tot.valoreAttuale)}</td>
                      <td className={`text-end ${gapClass(tot.gap)}`}>{formatEuro(tot.gap)}</td>
                      <td className={`text-end ${gapClass(tot.gapPerc)}`}>{formatPerc(tot.gapPerc)}</td>
                    </tr>
                  ))}
                </tfoot>
              )}
            </Table>
          </div>

          <div className="mt-2">
            <AppPaginator
              keyPaginator={StorageKeysEnum.CURRENT_PAGE_DASHBOARD_STORICO}
              currentPage={pageNumber}
              totalPages={totalPages}
              totalElements={rows.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPageNumber}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StoricoTable;
